export type TaskDashboardView = 'cards' | 'board' | 'list'
export type SortDirection = 'asc' | 'desc'

export interface TaskSortLevel {
  column: string
  direction: SortDirection
}

export interface TaskCardFields {
  item: boolean
  due_date: boolean
  funeral_home: boolean
  company: boolean
  responsable: boolean
  assigned: boolean
  created_at: boolean
  funeral_date: boolean
}

export interface TaskDashboardSettings {
  view: TaskDashboardView
  group: { column: string | null; direction: SortDirection }
  sort: TaskSortLevel[]
  fields: TaskCardFields
  visibleStatuses: number[]
  statusInteraction: 'cycle' | 'select'
  /** Keys of the groups the user collapsed, `__none__` for the group without value. */
  collapsedGroups: string[]
}

const VIEWS: TaskDashboardView[] = ['cards', 'board', 'list']
/** Groupings dropped after BBSW-11295 (BBSW-11710): saved tabs fall back on the deceased. */
const REMOVED_GROUP_COLUMNS = ['parent', 'dossier']
const REMOVED_GROUP_FALLBACK = 'deceased'
const MAX_SORT_LEVELS = 2

export const DEFAULT_TASK_DASHBOARD_SETTINGS: TaskDashboardSettings = {
  view: 'cards',
  group: { column: 'category_id', direction: 'asc' },
  sort: [
    { column: 'due_date', direction: 'asc' },
    { column: 'task_name', direction: 'asc' }
  ],
  fields: {
    item: true,
    due_date: true,
    funeral_home: true,
    company: false,
    responsable: false,
    assigned: true,
    created_at: true,
    funeral_date: false
  },
  visibleStatuses: [0, 1, 2],
  statusInteraction: 'cycle',
  collapsedGroups: []
}

const asDirection = (value: unknown): SortDirection =>
  value === 'desc' ? 'desc' : 'asc'

const asSortLevels = (value: unknown): TaskSortLevel[] =>
  Array.isArray(value)
    ? value
        .filter(
          (level): level is { column: string; direction?: unknown } =>
            typeof level?.column === 'string'
        )
        .slice(0, MAX_SORT_LEVELS)
        .map((level) => ({
          column: level.column,
          direction: asDirection(level.direction)
        }))
    : []

/** Key under which a group is remembered as collapsed: its key, or this one when it has none. */
export const NO_VALUE_GROUP_KEY = '__none__'

export const groupCollapseKey = (key: string | null): string =>
  key ?? NO_VALUE_GROUP_KEY

const FUNERAL_DAY_KEY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Drops the collapsed funeral days before today (YYYY-MM-DD): the API files them under
 * the `past` group, so they never come back. Without it, grouped by funeral date, every
 * collapsed day would stay in the saved settings forever.
 */
export const withoutElapsedFuneralDays = (
  collapsedGroups: string[],
  groupColumn: string | undefined,
  today: string
): string[] =>
  groupColumn === 'funeral_date'
    ? collapsedGroups.filter(
        (key) => !FUNERAL_DAY_KEY.test(key) || key >= today
      )
    : collapsedGroups

/**
 * Reads the `template_dashboards.sort` json of a task dashboard. Tenants still hold the
 * legacy shape `{group: 'categories'|'status', direction}` written before BBSW-11295.
 */
export function normalizeTaskDashboardSettings(
  raw: unknown
): TaskDashboardSettings {
  const source =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {}
  const defaults = DEFAULT_TASK_DASHBOARD_SETTINGS

  if (Object.keys(source).length === 0) {
    return structuredClone(defaults)
  }

  const isLegacy =
    typeof source.group === 'string' || source.group === undefined

  const legacyGroup = source.group === 'status' ? null : 'category_id'
  const legacyView: TaskDashboardView =
    source.group === 'status' ? 'board' : 'cards'
  // Legacy boards were ordered by last update (spec R4); the stored direction only ever
  // applied to the task name in categories mode, status boards ignored it.
  const legacySort: TaskSortLevel[] =
    source.group !== 'status' &&
    (source.direction === 'asc' || source.direction === 'desc')
      ? [{ column: 'task_name', direction: source.direction }]
      : [{ column: 'updated_at', direction: 'desc' }]

  const modernGroup = (
    source.group && typeof source.group === 'object' ? source.group : {}
  ) as Record<string, unknown>
  const group = isLegacy
    ? { column: legacyGroup, direction: 'asc' as SortDirection }
    : {
        column:
          typeof modernGroup.column !== 'string'
            ? null
            : REMOVED_GROUP_COLUMNS.includes(modernGroup.column)
              ? REMOVED_GROUP_FALLBACK
              : modernGroup.column,
        direction: asDirection(modernGroup.direction)
      }

  return {
    view: VIEWS.includes(source.view as TaskDashboardView)
      ? (source.view as TaskDashboardView)
      : isLegacy
        ? legacyView
        : defaults.view,
    group,
    sort:
      isLegacy && source.sort === undefined
        ? legacySort
        : asSortLevels(source.sort),
    fields: {
      ...defaults.fields,
      ...(typeof source.fields === 'object' && source.fields
        ? (source.fields as Partial<TaskCardFields>)
        : {})
    },
    visibleStatuses: Array.isArray(source.visibleStatuses)
      ? source.visibleStatuses.filter(
          (status): status is number => typeof status === 'number'
        )
      : [...defaults.visibleStatuses],
    statusInteraction:
      source.statusInteraction === 'select' ? 'select' : 'cycle',
    collapsedGroups: Array.isArray(source.collapsedGroups)
      ? source.collapsedGroups.filter(
          (key): key is string => typeof key === 'string'
        )
      : []
  }
}

/** List header click: same column inverts, another column becomes level one and keeps the previous one as level two. */
export function setPrimarySort(
  settings: TaskDashboardSettings,
  column: string
): TaskDashboardSettings {
  const [primary] = settings.sort

  if (primary?.column === column) {
    return {
      ...settings,
      sort: [
        { column, direction: primary.direction === 'asc' ? 'desc' : 'asc' },
        ...settings.sort.slice(1)
      ]
    }
  }

  const previous = settings.sort
    .filter((level) => level.column !== column)
    .slice(0, MAX_SORT_LEVELS - 1)

  return { ...settings, sort: [{ column, direction: 'asc' }, ...previous] }
}
